import { createEnumParam } from "serialize-query-params";
import {
  useQueryParams,
  type QueryParamOptions,
  withDefault,
  type QueryParamConfig,
} from "use-query-params";

import {
  CustomJsonParam,
  type ICompareDateRangeEnum,
  type DateRangeChoiceV2,
  type FixedDateInterval,
  type ITimeGranularityV2,
  TimeGranularityV2,
} from "@north-beam/nb-common";

type DateRangeType<T> = T | FixedDateInterval;

export const TimeGranularityParam = createEnumParam<ITimeGranularityV2>(
  TimeGranularityV2.keyOrder,
);

export const DateRangeParam =
  CustomJsonParam<DateRangeType<DateRangeChoiceV2>>();

export const CompareDateRangeParam =
  CustomJsonParam<DateRangeType<ICompareDateRangeEnum>>();

export enum DateRangeQueryParam {
  TIME_GRANULARITY = "timeGranularity",
  DATE_RANGE = "dateRange",
  COMPARE_DATE_RANGE = "compareDateRange",
}

type QueryParamValues = {
  [DateRangeQueryParam.TIME_GRANULARITY]: ITimeGranularityV2;
  [DateRangeQueryParam.COMPARE_DATE_RANGE]: DateRangeType<ICompareDateRangeEnum>;
  [DateRangeQueryParam.DATE_RANGE]: DateRangeType<DateRangeChoiceV2>;
};

type DateRangeQueryParams = {
  [key in DateRangeQueryParam]: QueryParamConfig<QueryParamValues[key]>;
};

const ALL_PARAMS: DateRangeQueryParams = {
  [DateRangeQueryParam.TIME_GRANULARITY]: withDefault(
    TimeGranularityParam,
    "daily" as const,
  ),
  [DateRangeQueryParam.COMPARE_DATE_RANGE]: withDefault(
    CompareDateRangeParam,
    "lastPeriod" as const,
  ),
  [DateRangeQueryParam.DATE_RANGE]: withDefault(
    DateRangeParam,
    "last7Days" as const,
  ),
};

type UseDateRangeQueryStateOptions = QueryParamOptions & {
  exclude?: Partial<{ [key in DateRangeQueryParam]: true }>;
  queryParams?: Partial<DateRangeQueryParams>;
};

type Params<O extends UseDateRangeQueryStateOptions> = O extends {
  exclude?: infer F;
}
  ? {
      [key in DateRangeQueryParam as F extends { [k in key]: true }
        ? never
        : key]: QueryParamConfig<QueryParamValues[key]>;
    }
  : DateRangeQueryParams;

/**
 * A hook that wraps "use-query-params"'s 'useQueryParams' such that query parameters related to
 * a date range, {@link DateRange}, time granularity, {@link TimeGranularity}, or compare date
 * range, {@link CompareDateRange} are used.
 *
 * The hook introduces an additional option, 'exclude', that allows certain query parameters related
 * to the aforementioned types to be excluded from the set.
 */
export const useDateRangeQueryState = <O extends UseDateRangeQueryStateOptions>(
  options?: O,
): ReturnType<typeof useQueryParams<Params<O>>> => {
  const { exclude, queryParams, ...rest } = options || {};
  const toUseParams = { ...ALL_PARAMS, ...(queryParams || {}) };
  const params = Object.keys(toUseParams).reduce(
    (prev: Params<O>, k: string): Params<O> => {
      const paramName = k as DateRangeQueryParam;
      if (exclude && exclude[paramName] === true) {
        return prev;
      }
      return { ...prev, [k]: toUseParams[paramName] };
    },
    {} as Params<O>,
  );
  return useQueryParams(params, rest);
};
